/* WeQuote Dev Board — free-type company picker with CRM matching (Lee, 28 Aug).
 * Never blocks typing. Suggestions (filtered as you type) come from:
 *   · CRM paid customers   · sales-CRM leads   · names already on the board
 * Picking a CRM entry attaches {crmId, crmKind} so the request links to the
 * real customer record; free-typed names save exactly as typed. */
import React, { useEffect, useRef, useState } from 'react'
import { fetchCustomers, type CrmCustomer } from '../core/api'

export interface CustomerPick { name: string; crmId?: number | string; crmKind?: 'paid' | 'deal' }

export const CustomerPicker: React.FC<{
  value: string
  onChange: (v: string) => void
  onPick: (p: CustomerPick) => void
  boardCompanies: string[]
  placeholder?: string
  autoFocus?: boolean
  pickedCrm?: { id: number | string; kind: 'paid' | 'deal' } | null
}> = ({ value, onChange, onPick, boardCompanies, placeholder, autoFocus, pickedCrm }) => {
  const [all, setAll] = useState<CrmCustomer[]>([])
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  useEffect(() => { fetchCustomers().then(setAll) }, [])
  useEffect(() => {
    const down = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', down)
    return () => document.removeEventListener('mousedown', down)
  }, [])
  const q = value.trim().toLowerCase()
  const crmNames = new Set(all.map((c) => c.name.toLowerCase()))
  const paid = all.filter((c) => c.kind === 'paid' && (!q || c.name.toLowerCase().includes(q))).slice(0, 8)
  const deals = all.filter((c) => c.kind === 'deal' && (!q || c.name.toLowerCase().includes(q))).slice(0, 6)
  const board = boardCompanies.filter((c) => !crmNames.has(c.toLowerCase()) && (!q || c.toLowerCase().includes(q))).slice(0, 5)
  const show = open && (paid.length + deals.length + board.length > 0)
  return (
    <div className="cp-wrap" ref={wrapRef}>
      <input
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder || 'Type a company name…'}
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setOpen(false) }}
      />
      {pickedCrm && <span className="cp-linked" title="Linked to the CRM record">CRM ✓</span>}
      {show && (
        <div className="cp-pop">
          {paid.length > 0 && <div className="cp-hd">Paid customers</div>}
          {paid.map((c) => (
            <div key={'p' + c.id} className="cp-opt" onMouseDown={(e) => { e.preventDefault(); onPick({ name: c.name, crmId: c.id, crmKind: 'paid' }); setOpen(false) }}>
              {c.name}<span className="cp-tag paid">CRM</span>
            </div>
          ))}
          {deals.length > 0 && <div className="cp-hd">CRM leads</div>}
          {deals.map((c) => (
            <div key={'d' + c.id} className="cp-opt" onMouseDown={(e) => { e.preventDefault(); onPick({ name: c.name, crmId: c.id, crmKind: 'deal' }); setOpen(false) }}>
              {c.name}<span className="cp-tag lead">Lead</span>
            </div>
          ))}
          {board.length > 0 && <div className="cp-hd">On this board</div>}
          {board.map((c) => (
            <div key={'b' + c} className="cp-opt" onMouseDown={(e) => { e.preventDefault(); onPick({ name: c }); setOpen(false) }}>{c}</div>
          ))}
        </div>
      )}
    </div>
  )
}
